// src/pages/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { query, where, getDocs, collection } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Modal from '../components/Modal';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';

/**
 * Admin Login Component
 * 
 * This component handles the authentication process for admin users.
 * It includes email/password validation, captcha verification, and role-based access control.
 * 
 * @param {Function} setUser - Function to update the user state in the parent component
 * @returns {JSX.Element} - Rendered login form with validation
 */
const Login = ({ setUser }) => {
  // State management for form inputs and UI
  const [email, setEmail] = useState(''); // Email input field state
  const [password, setPassword] = useState(''); // Password input field state
  const [showPassword, setShowPassword] = useState(false); // Toggle password visibility
  const [isLoading, setIsLoading] = useState(false); // Loading state during authentication
  const [captchaValue, setCaptchaValue] = useState(null); // Store captcha validation result
  const [attempted, setAttempted] = useState(false); // Track login attempts
  const [modal, setModal] = useState({ isVisible: false, title: "", message: "" }); // Modal for displaying messages
  
  const navigate = useNavigate(); // React Router navigation hook
  const captchaRef = useRef(null); // Reference to the captcha component for reset functionality
  const emailInputRef = useRef(null); // Reference to focus email input on load

  /**
   * Effect hook to check if user is already authenticated
   * Redirects to admin dashboard if user is already logged in
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        navigate('/admin'); // Redirect to admin dashboard if already authenticated
      }
    });
    
    // Focus email input on component mount
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
    
    // Cleanup subscription when component unmounts
    return () => unsubscribe();
  }, [navigate]);

  /**
   * Handle captcha validation change
   * @param {string} value - The captcha validation token
   */
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setAttempted(false); // Reset attempt status when captcha changes
  };

  /**
   * Reset captcha for new validation attempt
   */
  const resetCaptcha = () => {
    setCaptchaValue(null);
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
  };

  /**
   * Handle Enter key press to submit form
   * @param {Event} e - Keyboard event
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  /**
   * Main login handler function
   * Validates inputs, authenticates user, and checks admin permissions
   */
  const handleLogin = async () => {
    // Input validation
    if (!email || !password) {
      setModal({ isVisible: true, title: "Error", message: "Please enter both email and password." });
      resetCaptcha();
      setAttempted(false);
      return;
    }
  
    // Captcha validation
    if (!captchaValue || attempted) {
      setModal({ isVisible: true, title: "Error", message: "Please complete the captcha before logging in." });
      resetCaptcha();
      setAttempted(false);
      return;
    }

    setIsLoading(true); // Start loading state
    
    try {
      // Attempt Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User signed in:', user);

      // Check if user exists in the users collection and has admin role
      const userQuery = query(collection(db, 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(userQuery);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        console.log('User Data:', userData);

        // Verify user has admin privileges
        if (userData.userType === 'Admin') {
          // If user is admin, update user state and navigate to admin dashboard
          if (setUser && typeof setUser === 'function') {
            setUser(user);
          } else {
            console.error('setUser is not available or not a function');
          }
          navigate('/admin');
        } else {
          // If user is not admin, sign them out and show error
          await signOut(auth);
          if (setUser && typeof setUser === 'function') {
            setUser(null);
          }
          setModal({ isVisible: true, title: "Access Denied", message: "Access denied. Admins only." });
          console.warn('Access denied for user:', email);
        }
      } else {
        // If no user document found, sign out and show error
        await signOut(auth);
        if (setUser && typeof setUser === 'function') {
          setUser(null);
        }
        setModal({ isVisible: true, title: "Error", message: "No user document found for this email." });
        console.warn('No user document found for:', email);
      }
      
      setAttempted(true);
      resetCaptcha();
    } catch (error) {
      // Handle authentication errors
      console.error('Error during login:', error);
      
      // Map common Firebase error codes to user-friendly messages
      const errorMessages = {
        'auth/user-not-found': "No user found with this email.",
        'auth/wrong-password': "Incorrect password. Please try again.",
        'auth/too-many-requests': "Too many failed attempts. Please try again later.",
        'auth/invalid-email': "Invalid email format.",
        'auth/user-disabled': "This account has been disabled.",
        'auth/network-request-failed': "Network error. Please check your connection.",
        'default': "Login failed. Please check your credentials."
      };
      
      setModal({ 
        isVisible: true, 
        title: "Error", 
        message: errorMessages[error.code] || errorMessages.default 
      });
      
      resetCaptcha();
      setAttempted(true);
    } finally {
      setIsLoading(false); // End loading state regardless of outcome
    }
  };

  // Render login form with validation and captcha
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Admin Login</h2>
                <p className="text-indigo-100 text-sm mt-1">YouthXConnect Events Portal</p>
              </div>
              <div className="bg-white/20 p-3 rounded-full">
                <FaShieldAlt className="text-white" size={24} />
              </div>
            </div>
          </div>
          
          {/* Form */}
          <div className="p-8">
            <div className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400" />
                  </div>
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    required
                  />
                </div>
              </div>
              
              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="text-gray-500 hover:text-gray-700" />
                    ) : (
                      <FaEye className="text-gray-500 hover:text-gray-700" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Captcha */}
              <div className="flex justify-center pt-2 pb-4">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey="6Leys3EqAAAAAEjkHcbcAjaoJ1Bf4o1xcG8W1Hv9"
                  onChange={handleCaptchaChange}
                  theme="light"
                />
              </div>
              
              {/* Login Button */}
              <button
                type="button"
                disabled={!captchaValue || isLoading}
                onClick={handleLogin}
                className={`w-full py-3 px-4 flex justify-center items-center rounded-lg text-white font-medium transition-all duration-300 ${
                  !captchaValue || isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-md hover:shadow-lg'
                }`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Authenticating...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
              
              {/* Security Notice */}
              <div className="text-center text-sm text-gray-500 mt-4">
                <p>Secure admin access only.</p>
                <p>Unauthorized access attempts will be logged.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} YouthXConnect. All rights reserved.</p>
        </div>
      </motion.div>
      
      {/* Modal for displaying error messages and notifications */}
      <Modal
        isVisible={modal.isVisible}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ isVisible: false, title: "", message: "" })}
      />
    </div>
  );
};

export default Login;